import { createNormalize } from "@app/analytics/utils";

import { Row } from "@rui/Stack";

import { Bar, BarWrapper, HighlightedRange, Label, RangeLabel } from "./styles";
import Marker from "../Marker";

interface GradientWithPercent {
  color: React.CSSProperties["color"];
  percent?: string;
}

interface GradientRangeProps {
  /** Color gradient from left to right, with optional percents */
  gradient: GradientWithPercent[] | React.CSSProperties["color"][];
  highlightedRange?: [number, number];
  labels?: number[];
  marker?: { value: number; label?: string };
  range: [number, number];
  rangeLabels?: [string, string];
}

const createBackgroundFromGradient = (
  gradient: GradientRangeProps["gradient"]
): React.CSSProperties["background"] => {
  if (typeof gradient[0] === "string") {
    return `linear-gradient(to right, ${gradient.join(", ")})`;
  }

  const gradientString = (gradient as GradientWithPercent[])
    .map((g) => `${g.color}${g.percent ? ` ${g.percent}` : ""}`)
    .join(", ");
  return `linear-gradient(to right, ${gradientString})`;
};

const MAX_MARKER_CENTER = 98;
const RANGE_LABEL_OFFSET = "80px"; // 32px for each label + 8px padding on either side

const GradientRange = ({
  gradient,
  highlightedRange,
  labels,
  marker,
  range,
  rangeLabels,
}: GradientRangeProps) => {
  const background = createBackgroundFromGradient(gradient);
  const [rangeStart, rangeEnd] = range;
  const normalize = createNormalize(rangeStart, rangeEnd);

  // prevent marker from floating aimlessly before or after the bar
  const markerCenter = marker
    ? Math.min(Math.max(0, normalize(marker.value)), MAX_MARKER_CENTER)
    : 0;

  const highlightedRangeProps = {
    start: highlightedRange ? normalize(highlightedRange[0]) : 0,
    end: highlightedRange
      ? normalize(rangeEnd) - normalize(highlightedRange[1])
      : 0,
  };

  if (labels) {
    return (
      <Row variant="center" sx={{ pt: marker?.label ? 3 : 1, pb: 3.5 }}>
        <BarWrapper>
          <Bar sx={{ background }} />
          {highlightedRange && <HighlightedRange {...highlightedRangeProps} />}
          {labels.map((label) => (
            <Label sx={{ left: `${normalize(label)}%` }} key={label}>
              {label}
            </Label>
          ))}
          {marker && <Marker label={marker.label} value={markerCenter} />}
        </BarWrapper>
      </Row>
    );
  }

  return (
    <Row variant="center" sx={{ pt: marker?.label ? 2.5 : 0 }}>
      <RangeLabel>{rangeLabels?.[0] ?? rangeStart}</RangeLabel>
      <BarWrapper sx={{ maxWidth: `calc(100% - ${RANGE_LABEL_OFFSET})` }}>
        {highlightedRange && <HighlightedRange {...highlightedRangeProps} />}
        <Bar sx={{ background }} />
        {marker && <Marker label={marker.label} value={markerCenter} />}
      </BarWrapper>
      <RangeLabel>{rangeLabels?.[1] ?? rangeEnd}</RangeLabel>
    </Row>
  );
};

export default GradientRange;
